import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/base/Breadcrumb";
import { articles } from "@/mocks/articles";

const ARTICLE_IMAGES: Record<string, string> = {
  "guide-industrial-connector-selection": "https://readdy.ai/api/search-image?query=Professional%20engineer%20selecting%20industrial%20connectors%20from%20a%20display%20board%2C%20clean%20workshop%20background%2C%20warm%20neutral%20lighting%2C%20focused%20composition%2C%20technical%20B2B%20photography%20style&width=600&height=440&seq=art-guide-industrial&orientation=landscape",
  "waterproof-connector-application": "https://readdy.ai/api/search-image?query=Outdoor%20LED%20landscape%20lighting%20installation%20with%20waterproof%20connectors%2C%20garden%20at%20dusk%2C%20warm%20golden%20hour%20lighting%2C%20professional%20outdoor%20engineering%20photography%2C%20detailed%20connection%20points%20visible&width=600&height=440&seq=art-waterproof-app&orientation=landscape",
  "smart-home-connector-trends": "https://readdy.ai/api/search-image?query=Modern%20smart%20home%20living%20room%20with%20smart%20plugs%20and%20connected%20devices%2C%20clean%20interior%20design%2C%20warm%20ambient%20lighting%2C%20lifestyle%20technology%20photography%2C%20cozy%20modern%20atmosphere&width=600&height=440&seq=art-smart-home&orientation=landscape",
  "connector-maintenance-guide": "https://readdy.ai/api/search-image?query=Technician%20cleaning%20and%20inspecting%20electrical%20connectors%20at%20workbench%2C%20tools%20and%20cleaning%20supplies%20visible%2C%20workshop%20environment%2C%20bright%20task%20lighting%2C%20professional%20maintenance%20photography&width=600&height=440&seq=art-maintenance&orientation=landscape",
  "custom-connector-development": "https://readdy.ai/api/search-image?query=Engineering%20team%20discussing%20custom%20connector%20design%20with%203D%20CAD%20models%20on%20large%20display%20screen%2C%20modern%20R&width=600&height=440&seq=art-custom-dev&orientation=landscape",
  "construction-site-safety": "https://readdy.ai/api/search-image?query=Construction%20site%20electrical%20safety%20setup%20with%20distribution%20box%20and%20protected%20connectors%2C%20workers%20in%20safety%20gear%2C%20bright%20outdoor%20lighting%2C%20professional%20industrial%20safety%20photography%2C%20organized%20cable%20management&width=600&height=440&seq=art-construction-safety&orientation=landscape",
  "connector-quality-standards": "https://readdy.ai/api/search-image?query=Quality%20control%20laboratory%20with%20testing%20equipment%20for%20electrical%20connectors%2C%20technicians%20in%20white%20coats%20inspecting%20products%2C%20clean%20bright%20laboratory%20lighting%2C%20professional%20industrial%20photography%2C%20precision%20instruments%20visible&width=600&height=440&seq=art-quality&orientation=landscape",
  "future-of-connector-technology": "https://readdy.ai/api/search-image?query=Futuristic%20concept%20of%20smart%20connectors%20with%20glowing%20data%20visualization%2C%20clean%20tech%20laboratory%20background%2C%20warm%20orange%20accent%20lighting%2C%20innovation%20technology%20photography%2C%20abstract%20digital%20patterns&width=600&height=440&seq=art-future-tech&orientation=landscape",
};

const allCategories = [...new Set(articles.map((a) => a.category))];

export default function ArticlesPage() {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState<string | null>(null);

  const filtered = articles.filter((a) => {
    const matchSearch =
      !search ||
      a.title.includes(search) ||
      a.excerpt.includes(search) ||
      a.tags.some((tag) => tag.includes(search));
    const matchCategory = !activeCategory || a.category === activeCategory;
    return matchSearch && matchCategory;
  });

  return (
    <>
      <Navbar />
      <main className="pt-16">
        <section className="py-10 md:py-14 bg-background-100">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb items={[{ label: t("articles.page.title") }]} />
            <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mt-4 mb-3">
              {t("articles.page.title")}
            </h1>
            <p className="text-sm text-foreground-600">
              {t("articles.page.subtitle")}
            </p>
          </div>
        </section>

        <section className="py-8 md:py-12 bg-background-50">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            {/* Search + Filter */}
            <div className="flex flex-col sm:flex-row gap-4 mb-8">
              <div className="relative flex-1 max-w-md">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={t("articles.search.placeholder")}
                  className="w-full pl-9 pr-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                />
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  onClick={() => setActiveCategory(null)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium cursor-pointer whitespace-nowrap transition-all duration-200 ${
                    !activeCategory
                      ? "bg-primary-500 text-white"
                      : "bg-background-200/60 text-foreground-600 hover:bg-background-300"
                  }`}
                >
                  全部
                </button>
                {allCategories.map((cat) => (
                  <button
                    key={cat}
                    onClick={() => setActiveCategory(cat)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium cursor-pointer whitespace-nowrap transition-all duration-200 ${
                      activeCategory === cat
                        ? "bg-accent-500 text-white"
                        : "bg-background-200/60 text-foreground-600 hover:bg-background-300"
                    }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            </div>

            {/* Article Grid */}
            {filtered.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {filtered.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.slug}`}
                    className="group bg-background-50 rounded-lg border border-background-200/70 overflow-hidden cursor-pointer transition-all duration-300 hover:border-primary-200"
                  >
                    <div className="relative h-44 overflow-hidden">
                      <img
                        src={ARTICLE_IMAGES[article.id]}
                        alt={article.title}
                        title={article.title}
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                      <span className="absolute top-3 left-3 px-2.5 py-1 rounded-full text-xs font-medium bg-accent-500/90 text-white">
                        {article.category}
                      </span>
                    </div>
                    <div className="p-4">
                      <div className="flex items-center gap-3 text-xs text-foreground-500 mb-2">
                        <span>{article.date}</span>
                        <span>{article.readTime}</span>
                      </div>
                      <h3 className="text-sm font-semibold text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {article.title}
                      </h3>
                      <p className="text-xs text-foreground-600 line-clamp-2 leading-relaxed">
                        {article.excerpt}
                      </p>
                      <div className="flex flex-wrap gap-1.5 mt-3">
                        {article.tags.slice(0, 3).map((tag, idx) => (
                          <span
                            key={idx}
                            className="px-2 py-0.5 rounded-full text-xs bg-secondary-100 text-secondary-700"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-200 flex items-center justify-center">
                  <i className="ri-file-search-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-sm text-foreground-500">
                  {t("articles.noResults")}
                </p>
              </div>
            )}
          </div>
        </section>

        {/* Schema */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "CollectionPage",
              name: "技术资讯 - 久久插",
              description: "久久插技术团队分享行业洞见、选购指南与应用实践",
              url: "https://www.jiujiucha.com/articles",
              isPartOf: {
                "@type": "WebSite",
                name: "久久插",
                url: "https://www.jiujiucha.com/",
              },
              breadcrumb: {
                "@type": "BreadcrumbList",
                itemListElement: [
                  {
                    "@type": "ListItem",
                    position: 1,
                    name: "首页",
                    item: "https://www.jiujiucha.com/",
                  },
                  {
                    "@type": "ListItem",
                    position: 2,
                    name: "技术资讯",
                  },
                ],
              },
            }),
          }}
        />
      </main>
      <Footer />
    </>
  );
}